import { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { DatePicker } from "./DatePicker";
import { FileSignature, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const TIPI_COMUNI = [
    "Full-time Indet.",
    "Part-time Indet.",
    "P.T. Indet.",
    "Full-time a tempo determinato",
    "Part-time a tempo determinato",
    "Apprendistato",
    "Stage / Tirocinio",
    "Collaborazione",
];

const MOTIVI_COMUNI = [
    "Passaggio Full-time",
    "Passaggio Part-time",
    "Rinnovo contratto a termine",
    "Trasformazione contratto",
    "Aumento ore settimanali",
    "Riduzione ore settimanali",
    "Promozione",
    "Altro",
];

export const ContractChangeDialog = ({ open, onOpenChange, employee, onSaved }) => {
    const [tipo, setTipo] = useState("");
    const [ore, setOre] = useState("");
    const [scadenza, setScadenza] = useState("");
    const [decorrenza, setDecorrenza] = useState("");
    const [motivo, setMotivo] = useState("");
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!tipo.trim()) {
            toast.error("Indica il tipo di contratto");
            return;
        }
        if (!decorrenza) {
            toast.error("Indica la data di decorrenza");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/employees/${employee.id}/contracts`, {
                contratto_tipo: tipo.trim(),
                contratto_ore: ore ? Number(ore) : null,
                contratto_scadenza: scadenza || null,
                decorrenza,
                motivo: motivo.trim() || null,
            });
            toast.success("Modifica contratto registrata");
            // Reset form
            setTipo("");
            setOre("");
            setScadenza("");
            setDecorrenza("");
            setMotivo("");
            onSaved && onSaved();
            onOpenChange(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore durante il salvataggio");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="contract-change-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2 text-[#376DB3]">
                        <FileSignature className="h-5 w-5" />
                        Modifica contratto
                    </DialogTitle>
                    <DialogDescription>
                        {employee?.cognome} {employee?.nome} — matricola {employee?.matricola}.
                        Il contratto attuale verrà archiviato, e quello nuovo diventerà attivo dalla data di decorrenza.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div>
                        <Label htmlFor="ctr-tipo">Tipo contratto *</Label>
                        <Input
                            id="ctr-tipo"
                            list="tipi-contratto"
                            value={tipo}
                            onChange={(e) => setTipo(e.target.value)}
                            placeholder="Es. Full-time Indet."
                            className="mt-1.5"
                            data-testid="ctr-tipo"
                            required
                        />
                        <datalist id="tipi-contratto">
                            {TIPI_COMUNI.map((m) => <option key={m} value={m} />)}
                        </datalist>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                            <Label htmlFor="ctr-ore">Ore settimanali</Label>
                            <Input
                                id="ctr-ore"
                                type="number"
                                value={ore}
                                onChange={(e) => setOre(e.target.value)}
                                placeholder="40"
                                step="0.5"
                                className="mt-1.5"
                                data-testid="ctr-ore"
                            />
                        </div>
                        <div>
                            <Label htmlFor="ctr-scadenza">Scadenza (se a termine)</Label>
                            <div className="mt-1.5">
                                <DatePicker value={scadenza} onChange={setScadenza} placeholder="Lascia vuoto se indet." />
                            </div>
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="ctr-decorrenza">Decorrenza *</Label>
                        <div className="mt-1.5">
                            <DatePicker value={decorrenza} onChange={setDecorrenza} placeholder="Quando inizia il nuovo contratto" />
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="ctr-motivo">Motivo della modifica</Label>
                        <Input
                            id="ctr-motivo"
                            list="motivi-contratto"
                            value={motivo}
                            onChange={(e) => setMotivo(e.target.value)}
                            placeholder="Es. Passaggio Full-time"
                            className="mt-1.5"
                            data-testid="ctr-motivo"
                        />
                        <datalist id="motivi-contratto">
                            {MOTIVI_COMUNI.map((m) => <option key={m} value={m} />)}
                        </datalist>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ctr-cancel">
                        Annulla
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={saving || !tipo.trim() || !decorrenza}
                        className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                        data-testid="ctr-save"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />}
                        Registra modifica
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
